import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import PageHeader from '../../Common/PageHeader';
import Icons from '../../../../components/Common/Icons/Icons';
import Footer from '../../Layout/Footer';
import { INoticesData, noticesAccordion, noticesCol } from './Data';
import DatePickerWithRange from '../../../../components/Common/UI/DateRange';
import AccordionTable from '../../Common/AccordionTable';
import { Input } from '../../../../components/Common/UI/Input';

export interface IPeriod {
  from: string;
  to: string;
}

const currentYear = dayjs().year();
const currentDate = dayjs().format('MMM DD YYYY');

export const initialPeriod: IPeriod = {
  from: `Mar 20 ${currentYear - 3}`,
  to: `${currentDate}`,
};

const initialPeriodNotices: IPeriod = {
  ...initialPeriod,
  from: `Jan 01 ${currentYear - 1}`,
};

const SEARCH_SUGGESTIONS = ['Payroll'];

function Notices() {
  const [active, setActive] = useState<number | undefined>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(initialPeriod);
  const [searchVal, setSearchVal] = useState('');
  const [open, setOpen] = useState(false);

  const handleOnChange = (from?: Date, to?: Date) => {
    if (from && to) {
      setSelectedPeriod({
        from: dayjs(from).format('MMM DD YYYY'),
        to: dayjs(to).format('MMM DD YYYY'),
      });
    }
  };

  const handleActive = (index?: number) => {
    setActive(index);
  };

  const filtered = useMemo(() => {
    if (!searchVal.trim()) return [];

    return SEARCH_SUGGESTIONS.filter(item => item.toLowerCase().includes(searchVal.toLowerCase()));
  }, [searchVal]);

  const noticesData = useMemo(() => {
    let filteredGroups = noticesAccordion;

    if (!(selectedPeriod.from === initialPeriodNotices.from && selectedPeriod.to === initialPeriodNotices.to)) {
      filteredGroups = noticesAccordion.filter(({ year }) =>
        dayjs(`01-01-${year}`).isBetween(dayjs(selectedPeriod.from), dayjs(selectedPeriod.to), 'year', '[]'),
      );
    }
    if (searchVal.trim()) {
      const searchLower = searchVal.toLowerCase();

      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter(item => {
            if (searchLower === 'payroll') return item.type === 'Payroll';
            if (searchLower === 'bsd') return item.type === 'BSD';

            return item.description.toLowerCase().includes(searchLower);
          }),
        }))
        .filter(group => group.data.length > 0);
    } else {
      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter(item => !item.type),
        }))
        .filter(group => group.data.length > 0);
    }

    return filteredGroups;
  }, [selectedPeriod, searchVal]);

  return (
    <div>
      <PageHeader
        icon={<Icons.Check />}
        title="Notices"
        actions={
          <div className="flex items-center gap-4">
            <div className="relative max-w-60">
              <Input
                placeholder="Search"
                variant="home"
                inputClassName="border-[#FFFFFF]"
                value={searchVal}
                icon={<Icons.Search />}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onChange={e => {
                  setSearchVal(e.target.value);
                  setOpen(!!e.target.value);
                }}
              />

              {open && searchVal.trim() && filtered.length > 0 && (
                <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow">
                  {filtered.map(item => (
                    <div
                      key={item}
                      tabIndex={0}
                      role="button"
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                      onMouseDown={() => {
                        setSearchVal(item);
                        setOpen(false);
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DatePickerWithRange
              variant="transparent"
              from={dayjs(initialPeriodNotices.from).toDate()}
              to={dayjs(initialPeriodNotices.to).toDate()}
              handleOnChange={handleOnChange}
            />
          </div>
        }
      />
      <div className="px-[20px] grid gap-[20px]">
        {(noticesData || []).length ? (
          <>
            <AccordionTable<INoticesData>
              data={noticesData}
              tableCol={noticesCol}
              handleActive={handleActive}
              active={active}
            />
            <Footer />
          </>
        ) : (
          <div className="font-bold text-[18px] text-center my-[100px]">
            There is no statement available for this data range.
          </div>
        )}
      </div>
    </div>
  );
}

export default Notices;
